import type { ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

type ConfirmDialogProps = {
  onConfirm: () => void;
  title?: string;
  description?: ReactNode;
  confirmLabel?: string;
  /** Red confirm button. Defaults to true — most confirms guard a destructive action. */
  destructive?: boolean;
  /** Disables both buttons (e.g. while the confirmed mutation is running). */
  isPending?: boolean;
  /** Trigger element. Optional when the dialog is controlled via `open`. */
  children?: ReactNode;
  /** Controlled mode — open the dialog from code instead of a trigger. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function ConfirmDialog({
  onConfirm,
  title = "Are you absolutely sure?",
  description = "This action cannot be undone.",
  confirmLabel = "Confirm",
  destructive = true,
  isPending = false,
  children,
  open,
  onOpenChange,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {children && <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>}
      {/* Portaled, but React events still bubble to ancestors — stop them so a
          confirm inside a clickable table row doesn't trigger row navigation. */}
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: destructive ? "destructive" : "default" })}
            disabled={isPending}
            onClick={onConfirm}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
